import { formatDate } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LazyLoadFilters } from 'src/app/core/containers/search-container/search-container.component';
import {
  CommercialOperationalNoteSearch,
  CommercialOperationalNoteSearchConnection,
  CommercialOperationalNoteSortOrQuery,
} from 'src/app/model/commercial-services/commercial-operational-note';
import { getCorrectedDate } from 'src/app/utils/utils';

const COMMERCIAL_OPERATIONAL_NOTE_DATE_FORMAT = 'yyyy-MM-dd hh:mm aaa';
const COMMERCIAL_OPERATIONAL_NOTE_TEXT_FILTERS = [
  'facilityName',
  'facilityId',
  'category',
  'aorName',
  'contactName',
  'comments',
] as const;

@Injectable({
  providedIn: 'root',
})
export class CommercialOperationalNoteBuilderService {
  private readonly _translateService = inject(TranslateService);

  buildCommercialOperationalNoteSearchConnection(
    response: any
  ): CommercialOperationalNoteSearchConnection {
    if (!response || !Array.isArray(response.items)) {
      throw new Error(
        'Cannot build operational notes from an invalid response.'
      );
    }

    return {
      items: response.items.map((item: any) =>
        this.buildCommercialOperationalNoteSearch(item)
      ),
      totalRecords: response.totalRecords,
    };
  }

  buildCommercialOperationalNoteSearch(
    item: any
  ): CommercialOperationalNoteSearch {
    if (!item) {
      throw new Error(
        'Cannot build an operational note from a missing response.'
      );
    }

    const updatedAt = getCorrectedDate(item.updatedAt)!;
    const flexStart = getCorrectedDate(item.flexStart);
    const flexEnd = getCorrectedDate(item.flexEnd);

    return {
      id: item.id,
      facilityName: item.facilityName,
      facilityId: item.facilityId,
      category: item.category,
      aorName: item.aorName,
      contactName: item.contactName,
      comments: this.formatComments(item, flexStart, flexEnd),
      updatedAt,
      flexStart,
      flexEnd,
      flexType: item.flexType,
      flexAmount: item.flexAmount,
    };
  }

  buildCommercialOperationalNoteQuery(
    filters: LazyLoadFilters,
    startDate: Date,
    endDate: Date,
    businessUnits: string[]
  ): CommercialOperationalNoteSortOrQuery {
    const query: CommercialOperationalNoteSortOrQuery = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      businessUnits,
    };

    for (const field of COMMERCIAL_OPERATIONAL_NOTE_TEXT_FILTERS) {
      const metadata = filters[field];
      const filter = Array.isArray(metadata) ? metadata[0] : metadata;
      const value = filter?.value;
      if (value !== null && value !== undefined && value !== '') {
        query[field] = String(value).trim();
      }
    }
    return query;
  }

  private formatComments(
    item: any,
    flexStart: Date | null,
    flexEnd: Date | null
  ): string {
    if (item.category.toLowerCase() !== 'flex') {
      return item.comments ?? '';
    }

    const sections: string[] = [];
    if (
      item.comments !== null &&
      item.comments !== undefined &&
      item.comments !== ''
    ) {
      sections.push(item.comments);
    }

    const flexDetails: string[] = [];
    if (flexStart) {
      flexDetails.push(
        `${this._translateService.instant('COMMERCIAL_SERVICES.LABEL.FLEX_START')}: ${formatDate(
          flexStart,
          COMMERCIAL_OPERATIONAL_NOTE_DATE_FORMAT,
          'en-US'
        )}`
      );
    }
    if (flexEnd) {
      flexDetails.push(
        `${this._translateService.instant('COMMERCIAL_SERVICES.LABEL.FLEX_END')}: ${formatDate(
          flexEnd,
          COMMERCIAL_OPERATIONAL_NOTE_DATE_FORMAT,
          'en-US'
        )}`
      );
    }
    if (
      item.flexType !== null &&
      item.flexType !== undefined &&
      item.flexType !== ''
    ) {
      flexDetails.push(
        `${this._translateService.instant('COMMERCIAL_SERVICES.LABEL.FLEX_TYPE')}: ${item.flexType}`
      );
    }
    if (
      item.flexAmount !== null &&
      item.flexAmount !== undefined &&
      item.flexAmount !== ''
    ) {
      flexDetails.push(
        `${this._translateService.instant('COMMERCIAL_SERVICES.LABEL.FLEX_AMOUNT')}: ${item.flexAmount}`
      );
    }

    if (flexDetails.length > 0) {
      sections.push(flexDetails.join('\n'));
    }
    return sections.join('\n');
  }
}
